// Password reset view (WN12). Reachable while logged OUT (routed outside the auth/admin guards).
// Two modes in one page, matching the "forgot password" flow:
//   * NO token in the URL  → the REQUEST form: enter an email. The server ALWAYS answers 202 (no
//     user-enumeration), so we always show the same "if that email exists, a link was sent" notice.
//   * a token in the URL   → the CONFIRM form: set a new password. The emailed link is
//     `{dashboard}/#/reset?token=<raw>`, so the token rides the URL fragment; we also accept the
//     path-router `?token=` form. On success we redirect to /login with a success notice.

import { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../api/client';
import { ApiError } from '../api/client';

/** Recover the reset token from either the URL fragment (`#/reset?token=…`, the emailed link) or the
 *  path-router query (`/reset?token=…`). Returns '' when absent (→ request mode). */
export function readResetToken(search?: string, hash?: string): string {
  const h = hash ?? (typeof window !== 'undefined' ? window.location.hash : '');
  const frag = h.includes('?') ? h.slice(h.indexOf('?') + 1) : '';
  const fromHash = new URLSearchParams(frag).get('token');
  if (fromHash) return fromHash;
  const s = search ?? (typeof window !== 'undefined' ? window.location.search : '');
  return new URLSearchParams(s).get('token') ?? '';
}

/** Client-side rules for the new-password form (mirrors the server's ≥8 rule). */
export function validateNewPassword(next: string, confirm: string): string | null {
  if (next.length < 8) return 'New password must be at least 8 characters.';
  if (next !== confirm) return 'New passwords do not match.';
  return null;
}

/** Client-side rule for the request form. */
export function validateResetEmail(email: string): string | null {
  if (!email.trim() || !email.includes('@')) return 'Enter a valid email address.';
  return null;
}

export function Reset() {
  const [params] = useSearchParams();
  const token = useMemo(
    () => readResetToken(`?${params.toString()}`),
    [params],
  );
  return token ? <ConfirmForm token={token} /> : <RequestForm />;
}

function RequestForm() {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const invalid = validateResetEmail(email);
    if (invalid) {
      setErr(invalid);
      return;
    }
    setErr(null);
    setBusy(true);
    try {
      await api.passwordResetRequest(email.trim());
      // ALWAYS the same notice — the server never reveals whether the email exists.
      setSent(true);
    } catch (ex) {
      // A disabled feature (404) is the only real failure surface here.
      if (ex instanceof ApiError && ex.status === 404) {
        setErr('Password reset is not available. Contact an administrator.');
      } else {
        setErr(ex instanceof Error ? ex.message : 'request failed');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="login-screen">
      <div className="login-card">
        <div className="brand">
          <span className="brand-mark">◣◢</span>
          <span className="brand-name">mcpgraph</span>
        </div>
        <p className="login-sub">Reset your password</p>

        {sent ? (
          <>
            <p className="login-sub">
              If that email exists, a reset link has been sent. Check your inbox.
            </p>
            <p className="login-alt">
              <Link to="/login">Back to sign in</Link>
            </p>
          </>
        ) : (
          <>
            <form onSubmit={submit} className="login-form">
              <label>
                Email
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="username"
                  required
                />
              </label>
              {err && <div className="form-error">{err}</div>}
              <button type="submit" className="btn btn-primary" disabled={busy}>
                {busy ? 'Sending…' : 'Send reset link'}
              </button>
            </form>
            <p className="login-alt">
              Remembered it? <Link to="/login">Back to sign in</Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
}

function ConfirmForm({ token }: { token: string }) {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const invalid = validateNewPassword(password, confirm);
    if (invalid) {
      setErr(invalid);
      return;
    }
    setErr(null);
    setBusy(true);
    try {
      await api.passwordResetConfirm(token, password);
      setDone(true);
      // Redirect to login with the success notice after a beat.
      setTimeout(() => navigate('/login', { replace: true }), 1500);
    } catch (ex) {
      if (ex instanceof ApiError && ex.status === 400) {
        setErr('This reset link is invalid or has expired. Request a new one.');
      } else if (ex instanceof ApiError && ex.status === 404) {
        setErr('Password reset is not available. Contact an administrator.');
      } else {
        setErr(ex instanceof Error ? ex.message : 'reset failed');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="login-screen">
      <div className="login-card">
        <div className="brand">
          <span className="brand-mark">◣◢</span>
          <span className="brand-name">mcpgraph</span>
        </div>
        <p className="login-sub">Choose a new password</p>

        {done ? (
          <>
            <p className="login-sub">Password updated. Redirecting to sign in…</p>
            <p className="login-alt">
              <Link to="/login">Continue to sign in</Link>
            </p>
          </>
        ) : (
          <form onSubmit={submit} className="login-form">
            <label>
              New password
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                required
              />
            </label>
            <label>
              Confirm password
              <input
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                autoComplete="new-password"
                required
              />
            </label>
            {err && <div className="form-error">{err}</div>}
            <button type="submit" className="btn btn-primary" disabled={busy}>
              {busy ? 'Updating…' : 'Update password'}
            </button>
          </form>
        )}
        {!done && (
          <p className="login-alt">
            <Link to="/login">Back to sign in</Link>
          </p>
        )}
      </div>
    </div>
  );
}
